"use client";

import { useState } from "react";
import { captureReadingContact } from "@/lib/api/chat";
import { trackEvent } from "@/lib/analytics-events";
import { CAPTURE_POINTS, FORM_DELIVERY_CAPTURE_ENABLED } from "@/lib/contact-capture-points";

/**
 * Form-level, delivery-framed contact capture (ADR-0026, Lever 2).
 *
 * The kundali birth-details form is the first natural identity moment — the
 * seeker is already handing over DOB / time / place. We add ONE optional field
 * framed as DELIVERY ("send my kundali to WhatsApp/email"), never a gate: the
 * reading always renders on-screen regardless (rule 46 — never gate the value;
 * Activation surfaces minimize friction). This captures the seeker BEFORE they
 * leave, which the post-result value-moment opt-in cannot. Marketing consent is
 * a SEPARATE, unchecked-by-default opt-in — providing a contact is transactional
 * delivery; the checkbox is the marketing opt-in (recorded by contact.py into
 * vedic_marketing_contacts + vedic_consent_events).
 *
 * Reuses the existing consent-correct backend (captureReadingContact →
 * POST /readings/{id}/contact); no new endpoint.
 */

export type DeliveryLang = "en" | "hi";

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const PHONE_RE = /^\+?\d[\d\s-]{6,}$/;

/** Detect whether the typed value is an email or a phone (else null = ignore). */
export function deliveryContactKind(value: string): "email" | "phone" | null {
  const v = value.trim();
  if (EMAIL_RE.test(v)) return "email";
  if (PHONE_RE.test(v)) return "phone";
  return null;
}

/**
 * Fire-and-forget capture of a form-level delivery contact, after the kundali is
 * created (so a real reading_id exists). Soft-fail (rule 41) — a capture hiccup
 * must never affect the result the seeker is waiting on. No-op when the field is
 * empty or not a valid email/phone, so it never blocks or errors a submit.
 */
export async function persistDeliveryContact(
  readingId: string | undefined | null,
  value: string,
  consentMarketing: boolean
): Promise<void> {
  const kind = readingId ? deliveryContactKind(value) : null;
  if (!readingId || !kind) return;
  try {
    await captureReadingContact(readingId, {
      [kind]: value.trim(),
      consent_marketing: consentMarketing,
    });
    trackEvent("contact_captured", {
      reading_id: readingId,
      type: kind,
      placement: "kundali-form-delivery",
      capture_point: CAPTURE_POINTS.KUNDALI_FORM_DELIVERY,
      consent_marketing: consentMarketing,
    });
  } catch {
    /* soft-fail: never surface a capture error on the value path */
  }
}

const COPY: Record<DeliveryLang, { label: string; placeholder: string; consent: string }> = {
  en: {
    label: "Send my kundali to WhatsApp or email",
    placeholder: "WhatsApp number or email (optional)",
    consent: "Also send my weekly rashifal & upay",
  },
  hi: {
    label: "Apni kundali WhatsApp ya email par paayein",
    placeholder: "WhatsApp number ya email (optional)",
    consent: "Weekly rashifal aur upay bhi bhejein",
  },
};

/**
 * The optional delivery field + a separate, unchecked marketing-consent box.
 * Controlled by the parent (state lives in KundaliFormCard so handleSubmit can
 * read it). Rendered just above the submit button; purely additive (optional),
 * so it never adds friction to the proven kundali submit.
 */
export function DeliveryContactField({
  value,
  onChange,
  consent,
  onConsentChange,
  language,
}: {
  value: string;
  onChange: (v: string) => void;
  consent: boolean;
  onConsentChange: (v: boolean) => void;
  language: DeliveryLang;
}) {
  const t = COPY[language] ?? COPY.en;
  return (
    <div className="mt-1 flex flex-col gap-2">
      <label className="text-[13px] font-medium" style={{ color: "var(--ish-text-muted)" }}>
        {t.label}
      </label>
      <input
        type="text"
        inputMode="email"
        autoComplete="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t.placeholder}
        className="w-full rounded-xl px-3 py-2.5 text-[14px]"
        style={{ border: "1px solid var(--ish-border)", background: "var(--ish-cream)" }}
      />
      {value.trim() ? (
        <label
          className="flex items-center gap-2 text-[12px]"
          style={{ color: "var(--ish-text-muted)" }}
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => onConsentChange(e.target.checked)}
          />
          {t.consent}
        </label>
      ) : null}
    </div>
  );
}

/**
 * Owns the delivery-field state + the post-create persist call, so the kundali
 * form orchestrator only wires `field` (render) + `persist(readingId)` (fire in
 * handleSubmit) — keeping that big component small.
 */
export function useDeliveryCapture(language: DeliveryLang) {
  const [value, setValue] = useState("");
  const [consent, setConsent] = useState(false);
  // Hooks run unconditionally (stable order); the flag only gates the returned
  // field + persist. Disabled → render nothing + no-op persist, so flipping
  // FORM_DELIVERY_CAPTURE_ENABLED off instantly removes this capture point while
  // the value-moment + chat-inline captures keep running (rollback lever).
  if (!FORM_DELIVERY_CAPTURE_ENABLED) {
    return { field: null, persist: async (_readingId?: string | null) => {} };
  }
  return {
    field: (
      <DeliveryContactField
        value={value}
        onChange={setValue}
        consent={consent}
        onConsentChange={setConsent}
        language={language}
      />
    ),
    persist: (readingId: string | null | undefined) =>
      persistDeliveryContact(readingId, value, consent),
  };
}
